import { useCallback, useRef, useState } from 'react'
import { sellerApi } from '../api/endpoints'

/**
 * Brief J — drag-drop image / video uploader for one stone slot.
 *
 * Implements the two-step signed-URL flow: get URL from Cloud
 * Functions, PUT bytes directly to Storage with progress, then call
 * the confirm endpoint to persist the URL on the stone doc.
 *
 * Visual: thumbnail + replace / remove if a URL is set; drop zone
 * if not. Single-instance per stone × kind so the DOM stays simple.
 */
export interface MediaUploaderProps {
  stockId: string
  kind: 'image' | 'video'
  currentUrl: string | null
  onChange?: (url: string | null) => void
  /** Disables the input + buttons (used while a parent is loading). */
  disabled?: boolean
}

const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.heic', '.webp']
const VIDEO_EXTS = ['.mp4', '.mov', '.webm']
const MAX_IMAGE_BYTES = 50 * 1024 * 1024
const MAX_VIDEO_BYTES = 200 * 1024 * 1024

function extOf(name: string): string {
  const i = name.lastIndexOf('.')
  return i === -1 ? '' : name.slice(i).toLowerCase()
}

function bytesToHuman(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function MediaUploader({ stockId, kind, currentUrl, onChange, disabled }: MediaUploaderProps) {
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [hover, setHover] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const accept = kind === 'image' ? 'image/jpeg,image/png,image/heic,image/webp' : 'video/mp4,video/quicktime,video/webm'
  const allowedExts = kind === 'image' ? IMAGE_EXTS : VIDEO_EXTS
  const maxBytes = kind === 'image' ? MAX_IMAGE_BYTES : MAX_VIDEO_BYTES

  const handleFile = useCallback(async (file: File) => {
    setError(null)

    const ext = extOf(file.name)
    if (!allowedExts.includes(ext)) {
      setError(`File must be ${allowedExts.join(' / ')}`)
      return
    }
    if (file.size > maxBytes) {
      setError(`File too large (max ${bytesToHuman(maxBytes)})`)
      return
    }
    if (file.size === 0) {
      setError('File is empty')
      return
    }

    const contentType = file.type || (kind === 'image' ? 'image/jpeg' : 'video/mp4')

    setBusy(true)
    setProgress(0)
    try {
      const minted = await sellerApi.mediaUploadUrl(stockId, { kind, contentType, ext })

      // Direct PUT to Storage with XHR so we get progress events.
      // fetch() doesn't expose upload progress in browsers without
      // the experimental ReadableStream wiring.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open(minted.method || 'PUT', minted.uploadUrl, true)
        for (const [k, v] of Object.entries(minted.headers || {})) {
          xhr.setRequestHeader(k, v)
        }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100))
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Storage PUT failed (HTTP ${xhr.status})`))
        }
        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.send(file)
      })

      // Persist on Firestore.
      const confirmed = await sellerApi.mediaConfirm(stockId, { kind, url: minted.downloadUrl })
      const nextUrl = kind === 'image' ? confirmed.media.imageUrl : confirmed.media.videoUrl
      onChange?.(nextUrl)
      setProgress(100)
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || 'Upload failed')
    } finally {
      setBusy(false)
      // Brief progress flash so the user sees 100% before it disappears.
      setTimeout(() => setProgress(null), 600)
    }
  }, [stockId, kind, allowedExts, maxBytes, onChange])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setHover(false)
    if (disabled || busy) return
    const file = e.dataTransfer?.files?.[0]
    if (file) handleFile(file)
  }, [busy, disabled, handleFile])

  const onPick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    if (e.target) e.target.value = '' // allow re-selecting same file
  }, [handleFile])

  const onRemove = useCallback(async () => {
    if (!currentUrl) return
    if (!window.confirm(`Remove this ${kind} from ${stockId}?`)) return
    setBusy(true)
    setError(null)
    try {
      await sellerApi.mediaDelete(stockId, kind)
      onChange?.(null)
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || 'Remove failed')
    } finally {
      setBusy(false)
    }
  }, [stockId, kind, currentUrl, onChange])

  const label = kind === 'image' ? 'Image' : 'Video'

  return (
    <div className="rounded-lg border border-border bg-paper p-3" data-testid={`media-uploader-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-soft">{label}</p>
        {currentUrl && !busy && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="text-[11px] font-medium text-danger hover:underline disabled:opacity-40"
            data-testid={`media-remove-${kind}`}
          >
            Remove
          </button>
        )}
      </div>

      {currentUrl ? (
        <div className="space-y-2">
          {kind === 'image' ? (
            <img
              src={currentUrl}
              alt={`${stockId} ${kind}`}
              className="w-full h-40 object-cover rounded-md border border-border bg-page"
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3' }}
            />
          ) : (
            <video
              src={currentUrl}
              preload="metadata"
              controls
              className="w-full h-40 object-cover rounded-md border border-border bg-black"
            />
          )}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || busy}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-40"
          >
            Replace
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setHover(true) }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          disabled={disabled || busy}
          className={`block w-full h-32 rounded-md border-2 border-dashed transition flex items-center justify-center text-xs ${
            hover ? 'border-primary bg-primary/5 text-primary' : 'border-border text-soft hover:border-primary/60'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {busy ? `Uploading… ${progress ?? 0}%` : (
            <span>
              <span className="block font-semibold">Drop {label.toLowerCase()} or click to browse</span>
              <span className="block mt-0.5 text-[11px]">
                {allowedExts.join(' / ')} · max {bytesToHuman(maxBytes)}
              </span>
            </span>
          )}
        </button>
      )}

      {progress != null && busy && (
        <div className="mt-2 h-1 w-full rounded bg-page overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
            data-testid={`media-progress-${kind}`}
          />
        </div>
      )}

      {error && (
        <p className="mt-2 text-[11px] text-danger" data-testid={`media-error-${kind}`}>{error}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onPick}
        className="hidden"
        data-testid={`media-input-${kind}`}
        disabled={disabled || busy}
      />
    </div>
  )
}
